import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { productionService } from '../../services/api';
import CompletionModal from './CompletionModal';
import { toast } from "react-toastify";
import BatchMobileView from './BatchMobileView';
import BatchDesktopView from './BatchDesktopView';

const Production = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [pagination, setPagination] = useState({
                                                     currentPage: 1,
                                                     totalPages: 1,
                                                     total: 0
                                                 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({
                                             status: '',
                                             search: '',
                                             page: 1,
                                             showCompleted: false
                                         });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteBatchId, setDeleteBatchId] = useState(null);
    const [forceDelete, setForceDelete] = useState(false);
    const [expandedBatchId, setExpandedBatchId] = useState(null);
    const [completionModalOpen, setCompletionModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [completionQuantity, setCompletionQuantity] = useState(1);
    const [processingAction, setProcessingAction] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [newShipstationBatches, setNewShipstationBatches] = useState([]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importingBatch, setImportingBatch] = useState(false);

// Add function to check for new batches
    const checkNewShipstationBatches = async () => {
        try {
            const response = await productionService.checkNewShipstationBatches();
            if (response.new_batches_count > 0) {
                setNewShipstationBatches(response.new_batches);
            }
        } catch (error) {
            console.error("Error checking ShipStation batches:", error);
        }
    };

// Add to useEffect to check on component mount
    useEffect(() => {
        checkNewShipstationBatches();
        // Check every 5 minutes
        const interval = setInterval(checkNewShipstationBatches, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

// Add import function
    const handleImportBatch = async (batchId, importItems = false) => {
        try {
            setImportingBatch(true);
            const response = await productionService.importShipstationBatch({
                                                                                batch_id: batchId,
                                                                                import_items: importItems
                                                                            });

            toast.success(`Batch imported successfully${importItems ? ' with items' : ''}`);

            // Refresh the list
            loadBatches();

            // Remove imported batch from new batches list
            setNewShipstationBatches(prev => prev.filter(b => b.batch_id !== batchId));

            // Close modal if open
            setShowImportModal(false);

        } catch (error) {
            console.error("Error importing batch:", error);
            toast.error(error.response?.data?.message || "Failed to import batch");
        } finally {
            setImportingBatch(false);
        }
    };


    const loadBatches = async () => {
        try {
            setLoading(true);
            const response = await productionService.getProductionBatches(filter);
            setBatches(response.data.data);
            setPagination({
                              currentPage: response.data.current_page,
                              totalPages: response.data.last_page,
                              total: response.data.total
                          });
        } catch (error) {
            console.error("Error fetching production batches:", error);
            toast.error("Failed to load production batches");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {


        loadBatches();
    }, [filter]);

    const handleFilterChange = (key, value) => {
        setFilter(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= pagination.totalPages) {
            setFilter(prev => ({ ...prev, page: newPage }));
        }
    };

    const handleDeleteBatch = async () => {
        try {
            setProcessingAction(true);

            // Pass force_delete as an option to the deleteProductionBatch method
            const response = await productionService.deleteProductionBatch(deleteBatchId, {
                force_delete: forceDelete
            });

            toast.success(response.message || 'Batch deleted successfully');
            // Refresh data
            loadBatches();
        } catch (error) {
            console.error('Error deleting batch:', error);
            const errorMessage = error.response?.data?.message || 'Failed to delete batch';
            toast.error(errorMessage);
        } finally {
            setProcessingAction(false);
            setShowDeleteModal(false);
            setDeleteBatchId(null);
            setForceDelete(false);
        }
    };

    const toggleExpand = (batchId) => {
        setExpandedBatchId(expandedBatchId === batchId ? null : batchId);
    };

    const openCompletionModal = (batch, item, e) => {
        if (e) {
            e.stopPropagation();
        }
        setSelectedBatch(batch);
        setSelectedItem(item);
        setCompletionQuantity(1);
        setCompletionModalOpen(true);
    };

    const closeCompletionModal = () => {
        setCompletionModalOpen(false);
        setSelectedItem(null);
        setSelectedBatch(null);
    };

    const handleStartProduction = async (batch, item, e) => {
        if (e) {
            e.stopPropagation();
        }

        try {
            setProcessingAction(true);
            await productionService.startItemProduction(batch.id, item.id);
            const response = await productionService.getProductionBatches(filter);
            setBatches(response.data.data);
            toast.success("Production started successfully");
        } catch (error) {
            console.error("Error starting production:", error);
            toast.error("Failed to start production");
        } finally {
            setProcessingAction(false);
        }
    };

    const handleCompleteItem = async (batch, item, quantity = null, e = null) => {
        if (e) {
            e.stopPropagation();
        }

        try {
            setProcessingAction(true);
            if (quantity) {
                // Partial completion
                await productionService.updateItemCompletion(batch.id, item.id, { completed_quantity: quantity });
                toast.success("Item partially completed");
            } else {
                // Full completion
                await productionService.completeItem(batch.id, item.id);
                toast.success("Item fully completed");
            }
            const response = await productionService.getProductionBatches(filter);
            setBatches(response.data.data);
            closeCompletionModal();
        } catch (error) {
            console.error("Error completing item:", error);
            toast.error("Failed to complete item");
        } finally {
            setProcessingAction(false);
        }
    };

    const handleAllocateMaterials = async (batchId, itemId = null, e = null) => {
        if (e) {
            e.stopPropagation();
        }

        try {
            setProcessingAction(true);

            if (itemId) {
                await productionService.allocateItemMaterials(batchId, itemId);
                toast.success("Materials allocated for the item successfully");
            } else {
                await productionService.allocateMaterials(batchId);
                toast.success("Materials allocated for all items successfully");
            }

            const response = await productionService.getProductionBatches(filter);
            setBatches(response.data.data);
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Unknown error occurred";
            console.error("Error allocating materials:", error);
            toast.error("Error allocating materials: " + errorMessage);
        } finally {
            setProcessingAction(false);
        }
    };

    const renderStatusBadge = (status, missing_parts) => {
        let displayStatus = '';
        const statusMap = {
            'planned': 'PLANNED',
            'in_progress': 'IN_PROGRESS',
            'completed': 'COMPLETED',
            'cancelled': 'CANCELLED',
            'pending': 'PENDING',
            'materials_allocated': 'MATERIALS ALLOCATED',
            'in_production': 'IN PRODUCTION',
            'missing_parts': 'MISSING PARTS',
        };

        if(missing_parts && missing_parts.length > 0) {
            displayStatus = 'MISSING PARTS';

        } else {
            displayStatus = statusMap[status] || status.toUpperCase();
        }


        const classes = {
            'PLANNED': 'bg-gray-100 text-gray-800',
            'IN_PROGRESS': 'bg-yellow-100 text-yellow-800',
            'COMPLETED': 'bg-green-100 text-green-800',
            'CANCELLED': 'bg-red-100 text-red-800',
            'PENDING': 'bg-gray-100 text-gray-800',
            'MATERIALS ALLOCATED': 'bg-blue-100 text-blue-800',
            'IN PRODUCTION': 'bg-purple-100 text-purple-800',
            'MISSING PARTS': 'bg-red-900 text-red-100',
        };

        return (
            <span className={`px-2 py-1 rounded-full text-[9px] sm:text-xs  font-medium ${classes[displayStatus] || 'bg-gray-100 text-gray-800'}`}>
                {displayStatus.replace('_', ' ')}
            </span>
        );
    };

    // Calculate total quantities for each batch
    const calculateBatchQuantities = (batch) => {
        if (!batch.items || batch.items.length === 0) {
            return { planned: 0, completed: 0 };
        }

        const planned = batch.items.reduce((sum, item) => sum + item.quantity, 0);
        const completed = batch.items.reduce((sum, item) => sum + (item.completed_quantity || 0), 0);

        return { planned, completed };
    };



    const renderActionButtons = (batch, item, isMobileView) => {
        if (item.status === 'pending' && item.missing_parts.length <= 0) {
            return (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleAllocateMaterials(batch.id, item.id, e);
                    }}
                    disabled={processingAction}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
                >
                    {isMobileView ? 'Allocate' : 'Allocate Materials'}
                </button>
            );
        } else if (item.status === 'materials_allocated') {
            return isMobileView ? (
                <div className="flex flex-col space-y-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleStartProduction(batch, item, e);
                        }}
                        disabled={processingAction}
                        className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 disabled:opacity-50"
                    >
                        Start
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openCompletionModal(batch, item, e);
                        }}
                        disabled={processingAction}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 disabled:opacity-50"
                    >
                        Complete
                    </button>
                </div>
            ) : (
                       <div className="flex space-x-2">
                           <button
                               onClick={(e) => {
                                   e.stopPropagation();
                                   handleStartProduction(batch, item, e);
                               }}
                               disabled={processingAction}
                               className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 disabled:opacity-50"
                           >
                               Start Production
                           </button>
                           <button
                               onClick={(e) => {
                                   e.stopPropagation();
                                   openCompletionModal(batch, item, e);
                               }}
                               disabled={processingAction}
                               className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 disabled:opacity-50"
                           >
                               Complete
                           </button>
                       </div>
                   );
        } else if (item.status === 'in_production') {
            return isMobileView ? (
                <div className="flex flex-col space-y-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openCompletionModal(batch, item, e);
                        }}
                        disabled={processingAction}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 disabled:opacity-50"
                    >
                        Update
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCompleteItem(batch, item, null, e);
                        }}
                        disabled={processingAction}
                        className="px-2 py-1 text-xs bg-green-800 text-white rounded hover:bg-green-900 focus:outline-none focus:ring-2 focus:ring-green-700 focus:ring-offset-1 disabled:opacity-50"
                    >
                        Complete All
                    </button>
                </div>
            ) : (
                       <div className="flex space-x-2">
                           <button
                               onClick={(e) => {
                                   e.stopPropagation();
                                   openCompletionModal(batch, item, e);
                               }}
                               disabled={processingAction}
                               className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 disabled:opacity-50"
                           >
                               Update Completion
                           </button>
                           <button
                               onClick={(e) => {
                                   e.stopPropagation();
                                   handleCompleteItem(batch, item, null, e);
                               }}
                               disabled={processingAction}
                               className="px-2 py-1 text-xs bg-green-800 text-white rounded hover:bg-green-900 focus:outline-none focus:ring-2 focus:ring-green-700 focus:ring-offset-1 disabled:opacity-50"
                           >
                               Complete All
                           </button>
                       </div>
                   );
        } else if (item.status === 'completed') {
            return (
                <span className="text-xs text-green-600 font-medium">Completed</span>
            );
        } else {
            return null;
        }
    };

    return (
        <div className="container mx-auto px-0 sm:px-4 py-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-0">Production Batches</h1>
                <button
                    onClick={() => navigate('/production/new')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center w-full md:w-auto justify-center"
                >
                    <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    New Batch
                </button>
            </div>
            {newShipstationBatches.length > 0 && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    {newShipstationBatches.length} new {newShipstationBatches.length === 1 ? 'batch is' : 'batches are'} available to import from ShipStation
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowImportModal(true)}
                            className="ml-4 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            View & Import
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow">
                <div className="px-4 md:px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg md:text-xl font-semibold text-gray-800">Production Schedule</h2>
                </div>

                <div className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:flex-wrap gap-4 mb-6">
                        <div className="w-full md:w-64">
                            <input
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Search by batch number..."
                                value={filter.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                            />
                        </div>
                        <div className="w-full md:w-48">
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filter.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="planned">Planned</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                        <div className="flex items-center">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filter.showCompleted}
                                    onChange={(e) => handleFilterChange('showCompleted', e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-700">Show Completed</span>
                            </label>
                        </div>
                        <button
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={() => setFilter({ status: '', search: '', page: 1 })}
                        >
                            Reset Filters
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : batches.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No production batches found
                        </div>
                    ) : isMobile ? (
                        <BatchMobileView
                            batches={batches}
                            expandedBatchId={expandedBatchId}
                            toggleExpand={toggleExpand}
                            renderStatusBadge={renderStatusBadge}
                            calculateBatchQuantities={calculateBatchQuantities}
                            renderActionButtons={renderActionButtons}
                            handleAllocateMaterials={handleAllocateMaterials}
                            navigate={navigate}
                            processingAction={processingAction}
                            setDeleteBatchId={setDeleteBatchId}  // Add this
                            setShowDeleteModal={setShowDeleteModal}  // Add this
                        />
                    ) : (
                            <BatchDesktopView
                                batches={batches}
                                expandedBatchId={expandedBatchId}
                                toggleExpand={toggleExpand}
                                renderStatusBadge={renderStatusBadge}
                                calculateBatchQuantities={calculateBatchQuantities}
                                renderActionButtons={renderActionButtons}
                                handleAllocateMaterials={handleAllocateMaterials}
                                navigate={navigate}
                                processingAction={processingAction}
                                setDeleteBatchId={setDeleteBatchId}  // Add this
                                setShowDeleteModal={setShowDeleteModal}  // Add this
                            />
                        )}

                    {/* Pagination */}
                    {!loading && batches.length > 0 && (
                        <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
                            <div className="text-sm text-gray-700 text-center md:text-left w-full md:w-auto">
                                Showing <span className="font-medium">{pagination.total > 0 ? (pagination.currentPage - 1) * (filter.per_page || 15) + 1 : 0}</span> to <span className="font-medium">{Math.min(pagination.currentPage * (filter.per_page || 15), pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage <= 1}
                                    className={`px-3 py-1 rounded-md ${
                                        pagination.currentPage <= 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage >= pagination.totalPages}
                                    className={`px-3 py-1 rounded-md ${
                                        pagination.currentPage >= pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Completion Modal */}
            {completionModalOpen && selectedItem && selectedBatch && (
                <CompletionModal
                    isOpen={completionModalOpen}
                    onClose={closeCompletionModal}
                    batch={selectedBatch}
                    item={selectedItem}
                    onComplete={handleCompleteItem}
                    processingAction={processingAction}
                />
            )}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete this production batch? This will return any allocated materials to inventory.
                        </p>

                        <div className="mb-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={forceDelete}
                                    onChange={e => setForceDelete(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <span className="ml-2 text-sm text-gray-600">
                        Force delete (also remove completed items from stock)
                    </span>
                            </label>
                            {forceDelete && (
                                <p className="text-sm text-red-600 mt-1">
                                    Warning: This will also remove any completed products from stock and return all materials to inventory.
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteBatchId(null);
                                    setForceDelete(false);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteBatch}
                                disabled={processingAction}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                            >
                                {processingAction ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full p-6 shadow-xl max-h-[80vh] overflow-y-auto">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Import ShipStation Batches</h3>

                        <div className="space-y-3">
                            {newShipstationBatches.map((batch) => (
                                <div key={batch.batch_id} className="border rounded-lg p-4 hover:bg-gray-50">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-medium">{batch.batch_number}</h4>
                                            <p className="text-sm text-gray-600 mt-1">
                                                ID: {batch.batch_id} | Items: {batch.count} | Created: {format(new Date(batch.created_at), 'MM/dd/yyyy')}
                                            </p>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleImportBatch(batch.batch_id, false)}
                                                disabled={importingBatch}
                                                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                                            >
                                                Import Batch Only
                                            </button>
                                            <button
                                                onClick={() => handleImportBatch(batch.batch_id, true)}
                                                disabled={importingBatch}
                                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                Import with Items
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowImportModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Production;
